import React, { useState, useMemo } from 'react';
import { Loader2, Search, Send, RefreshCw, CheckCircle2, Circle, AlertCircle, ExternalLink, Ban, Layers, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';

// Table of products eligible to list on eBay, each with a synced / not-synced
// indicator. Admins tick the rows they want to push and hit "Send selected".
export default function EbayProductSyncTable() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const SORTERS = {
    name: (a, b) => (a.name || '').localeCompare(b.name || ''),
    sku: (a, b) => (a.sku || '').localeCompare(b.sku || ''),
    price: (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
    ebay_category_id: (a, b) => (a.ebay_category_id || '').localeCompare(b.ebay_category_id || ''),
    ebay_status: (a, b) => Number(!!a.ebay_listed) - Number(!!b.ebay_listed),
  };
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const SortHead = ({ k, label, className = '' }) => {
    const active = sort.key === k;
    return (
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 hover:text-white transition-colors ${active ? 'text-white' : 'text-white/30'} ${className}`}
      >
        {label}
        {active ? (sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ChevronsUpDown className="w-3 h-3 opacity-50" />}
      </button>
    );
  };

  const updateOne = async (p) => {
    setBusyId(p.id);
    try {
      const res = await base44.functions.invoke('ebaySyncListings', { product_ids: [p.id] });
      const d = res.data || {};
      if (d.error) {
        toast({ variant: 'destructive', description: d.error });
      } else if (d.synced) {
        toast({ description: `Updated "${p.name}" on eBay.` });
      } else if (d.failures?.length) {
        toast({ variant: 'destructive', description: `Update failed: ${d.failures[0].error}` });
      }
      qc.invalidateQueries({ queryKey: ['ebay-sync-products'] });
    } catch (e) {
      toast({ variant: 'destructive', description: e?.message || 'Update failed.' });
    } finally {
      setBusyId(null);
    }
  };

  const endOne = async (p) => {
    if (!confirm(`End the eBay listing for "${p.name}"? This removes it from eBay but keeps the product in your store.`)) return;
    setBusyId(p.id);
    try {
      const res = await base44.functions.invoke('ebayEndListing', { product_id: p.id });
      const d = res.data || {};
      if (d.error) {
        toast({ variant: 'destructive', description: d.error });
      } else {
        toast({ description: `Ended the eBay listing for "${p.name}".` });
      }
      qc.invalidateQueries({ queryKey: ['ebay-sync-products'] });
    } catch (e) {
      toast({ variant: 'destructive', description: e?.message || 'Failed to end listing.' });
    } finally {
      setBusyId(null);
    }
  };

  const publishGroup = async (p) => {
    const group_key = p.ebay_group_key || '';
    const parent_item_id = p.square_parent_item_id || '';
    if (!group_key && !parent_item_id) {
      toast({ variant: 'destructive', description: 'This variant has no group key or Square parent item id.' });
      return;
    }
    setBusyId(p.id);
    try {
      const res = await base44.functions.invoke('ebaySyncVariationGroup', { group_key: group_key || undefined, parent_item_id: parent_item_id || undefined });
      const d = res.data || {};
      if (d.error) {
        toast({ variant: 'destructive', description: d.error });
      } else {
        toast({ description: `Published ${d.variants || 0} variants as one eBay listing (${d.aspect || 'Colour'}: ${(d.aspect_values || []).join(', ')}).` });
      }
      qc.invalidateQueries({ queryKey: ['ebay-sync-products'] });
    } catch (e) {
      toast({ variant: 'destructive', description: e?.message || 'Group publish failed.' });
    } finally {
      setBusyId(null);
    }
  };

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['ebay-sync-products'],
    queryFn: () => base44.entities.Product.list('-updated_date', 500),
  });

  // Show published+sellable products; flag whether each is eBay-eligible.
  const rows = useMemo(() => {
    const q = search.toLowerCase();
    return products
      .filter((p) => p.published !== false && p.sellable !== false)
      .filter((p) => !q || (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q))
      .map((p) => ({
        ...p,
        eligible: !!(p.ebay_category_id && typeof p.price === 'number' && p.price > 0),
      }))
      .sort((a, b) => {
        const fn = SORTERS[sort.key] || SORTERS.name;
        return sort.dir === 'asc' ? fn(a, b) : fn(b, a);
      });
  }, [products, search, sort]);

  // Variation groups: variants sharing a group key (Square parent item token
  // or an explicit ebay_group_key) publish as ONE multi-variation listing.
  const groupKeyFor = (p) => p.ebay_group_key || (p.is_variant && p.square_parent_item_id) || null;
  const groups = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      if (p.published === false || p.sellable === false) continue;
      const key = groupKeyFor(p);
      if (!key) continue;
      const eligible = !!(p.ebay_category_id && typeof p.price === 'number' && p.price > 0);
      if (!map.has(key)) map.set(key, { key, eligibleCount: 0, count: 0, aspect: p.ebay_variation_aspect || 'Colour' });
      const g = map.get(key);
      g.count++;
      if (eligible) g.eligibleCount++;
      g.aspect = p.ebay_variation_aspect || g.aspect;
    }
    return map;
  }, [products]);

  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const send = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setSending(true);
    try {
      const res = await base44.functions.invoke('ebaySyncListings', { product_ids: ids });
      const d = res.data || {};
      const synced = d.synced || 0;
      const failed = d.failures || [];
      if (d.error) {
        toast({ variant: 'destructive', description: d.error });
      } else if (failed.length) {
        toast({ variant: 'destructive', description: `Synced ${synced}, ${failed.length} failed — see table.` });
      } else {
        toast({ description: `Listed ${synced} product${synced === 1 ? '' : 's'} on eBay.` });
      }
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['ebay-sync-products'] });
    } catch (e) {
      toast({ variant: 'destructive', description: e?.message || 'Listing sync failed.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#111115] border border-white/10 p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Products → eBay</h3>
          <p className="text-xs text-white/50 mt-1">
            Select products to push as live fixed-price eBay listings. Items need an eBay category set.
          </p>
        </div>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['ebay-sync-products'] })}
          className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white border border-white/10 hover:border-white/20 px-3 py-1.5 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search product or SKU..."
            className="bg-[#0d0d0f] border-white/10 text-white pl-9 h-10 placeholder:text-white/20 focus:border-primary/60"
          />
        </div>
        <button
          onClick={send}
          disabled={sending || selected.size === 0}
          className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider h-10 px-4 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send {selected.size > 0 ? `(${selected.size})` : ''} to eBay
        </button>
      </div>

      {/* Table */}
      <div className="mt-4 border border-white/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0d0d0f] border-b border-white/5">
              <th className="w-10 px-3 py-2.5 text-left">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleAll}
                  disabled={rows.length === 0}
                  className="accent-primary w-4 h-4"
                />
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold tracking-widest"><SortHead k="name" label="PRODUCT" /></th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold tracking-widest hidden md:table-cell"><SortHead k="sku" label="SKU" /></th>
              <th className="px-3 py-2.5 text-right text-[10px] font-bold tracking-widest hidden sm:table-cell"><SortHead k="price" label="PRICE" className="flex-row-reverse" /></th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold tracking-widest hidden lg:table-cell"><SortHead k="ebay_category_id" label="EBAY CAT" /></th>
              <th className="px-3 py-2.5 text-center text-[10px] font-bold tracking-widest"><SortHead k="ebay_status" label="EBAY STATUS" className="justify-center" /></th>
              <th className="px-3 py-2.5 text-center text-[10px] font-bold tracking-widest text-white/30">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="text-center py-10"><Loader2 className="w-5 h-5 text-primary animate-spin mx-auto" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-white/30 text-xs">No products found</td></tr>
            ) : rows.map((p) => {
              const synced = !!p.ebay_listed;
              const groupKey = groupKeyFor(p);
              const group = groupKey ? groups.get(groupKey) : null;
              const isVariant = !!groupKey;
              return (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                      disabled={!p.eligible}
                      className="accent-primary w-4 h-4 disabled:opacity-30"
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="w-8 h-8 object-cover border border-white/10 shrink-0" />
                      ) : (
                        <span className="w-8 h-8 bg-white/5 border border-white/10 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <span className="text-white truncate max-w-[240px] block">{p.name || '—'}</span>
                        {isVariant && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[9px] font-bold tracking-wider text-blue-400/80">
                            <span className="w-1 h-1 rounded-full bg-blue-400" /> VARIATION · {group?.aspect || p.ebay_variation_aspect || 'Colour'}
                            {p.option_value ? <span className="text-white/30 font-normal normal-case">— {p.option_value}</span> : null}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-white/50 font-mono text-xs hidden md:table-cell">{p.sku || '—'}</td>
                  <td className="px-3 py-2.5 text-right text-white/70 hidden sm:table-cell">£{Number(p.price || 0).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-white/50 text-xs hidden lg:table-cell">{p.ebay_category_id || '—'}</td>
                  <td className="px-3 py-2.5 text-center">
                    {!p.eligible ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-white/30">
                        <AlertCircle className="w-3 h-3" /> NO CATEGORY
                      </span>
                    ) : synced ? (
                      p.ebay_listing_id ? (
                        <a
                          href={`https://www.ebay.co.uk/itm/${p.ebay_listing_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-emerald-400 hover:text-emerald-300"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CheckCircle2 className="w-3 h-3" /> SYNCED <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ) : isVariant ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-emerald-400" title="Published as part of a multi-variation listing">
                          <Layers className="w-3 h-3" /> GROUP
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-emerald-400">
                          <CheckCircle2 className="w-3 h-3" /> SYNCED
                        </span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-white/40">
                        <Circle className="w-3 h-3" /> NOT SYNCED
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-center gap-1.5">
                      {isVariant ? (
                        <button
                          onClick={() => publishGroup(p)}
                          disabled={busyId === p.id || !group || group.eligibleCount < 2}
                          title={group && group.eligibleCount >= 2 ? `Publish ${group.eligibleCount} variants as one multi-variation listing` : 'Need 2+ eligible variants in the group'}
                          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-blue-300/90 hover:text-blue-300 border border-blue-400/20 hover:border-blue-400/40 px-2 py-1 transition-colors disabled:opacity-40"
                        >
                          {busyId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
                          {p.ebay_listed ? 'UPDATE GROUP' : 'PUBLISH GROUP'}
                        </button>
                      ) : p.eligible ? (
                        <button
                          onClick={() => updateOne(p)}
                          disabled={busyId === p.id}
                          title={p.ebay_listed ? 'Update eBay listing' : 'List on eBay'}
                          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-white/60 hover:text-white border border-white/10 hover:border-white/20 px-2 py-1 transition-colors disabled:opacity-40"
                        >
                          {busyId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                          {p.ebay_listed ? 'UPDATE' : 'LIST'}
                        </button>
                      ) : null}
                      {p.ebay_listed && (
                        <button
                          onClick={() => endOne(p)}
                          disabled={busyId === p.id}
                          title="End eBay listing"
                          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-red-400/80 hover:text-red-400 border border-red-400/20 hover:border-red-400/40 px-2 py-1 transition-colors disabled:opacity-40"
                        >
                          <Ban className="w-3 h-3" /> END
                        </button>
                      )}
                    </div>
                  </td>
                  </tr>
                  );
                  })}
                  </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-white/30">
        {rows.length} products · {rows.filter((r) => r.eligible).length} eligible · {rows.filter((r) => r.ebay_listed).length} synced to eBay
      </p>
    </div>
  );
}